import type { PinnacleFileHealthDTO } from '../types'
import type { ColumnsType } from 'antd/es/table'

import {
  ReloadOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import {
  Table,
  Card,
  Tag,
  Space,
  Typography,
  Tooltip,
  Button,
  Row,
  Col,
  Statistic,
  Alert
} from 'antd'

import { usePinnacleHealth } from '../hooks'
import { fmtDateTime } from '../utils'

const { Title } = Typography

const FilesTable: React.FC<{
  data: PinnacleFileHealthDTO[]
  expectedEnvs: string[]
  loading: boolean
  onReload: () => void
}> = ({ data, expectedEnvs, loading, onReload }) => {
  const columns: ColumnsType<PinnacleFileHealthDTO> = [
    {
      title: 'File Name',
      dataIndex: 'name',
      key: 'name',
      render: (v, r) => (
        <Space direction='vertical' size={0}>
          <Space>
            <FileTextOutlined />
            <Typography.Text strong>{v}</Typography.Text>
          </Space>
          <Typography.Text type='secondary' style={{ fontSize: 12 }}>
            created {fmtDateTime(r.created_at)}
          </Typography.Text>
        </Space>
      )
    },
    {
      title: 'Processed',
      dataIndex: 'processed',
      key: 'processed',
      align: 'center',
      width: 120,
      render: (v: boolean) =>
        v ? (
          <Tag color='green' icon={<CheckCircleOutlined />}>
            Yes
          </Tag>
        ) : (
          <Tag color='orange' icon={<ClockCircleOutlined />}>
            Pending
          </Tag>
        )
    },
    {
      title: 'Environments',
      key: 'environments',
      render: (_, r) => (
        <Space wrap>
          {expectedEnvs.map((env, index) => {
            // Paths are stored in order: dev (0), prod (1), qa (2)
            const hasEnv = r.s3_paths.length > index && r.s3_paths[index]
            return (
              <Tag key={env} color={hasEnv ? 'green' : 'red'}>
                {env.toUpperCase()}: {hasEnv ? '✓' : '✗'}
              </Tag>
            )
          })}
        </Space>
      )
    },
    {
      title: 'Status',
      key: 'status',
      align: 'center',
      width: 150,
      render: (_, r) =>
        r.is_healthy ? (
          <Tag color='success' icon={<CheckCircleOutlined />}>
            Healthy
          </Tag>
        ) : (
          <Tooltip title={`Missing: ${r.missing_paths.join(', ')}`}>
            <Tag color='error' icon={<WarningOutlined />}>
              Missing Copies
            </Tag>
          </Tooltip>
        )
    }
  ]

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={5} style={{ margin: 0 }}>
            Recent Files (Last 7 Days)
          </Typography.Title>
          <Tooltip title='Reload'>
            <Button icon={<ReloadOutlined />} onClick={onReload} size='small' />
          </Tooltip>
        </Space>
      }
      className='rounded-2xl'
    >
      <Table<PinnacleFileHealthDTO>
        rowKey={r => r.id}
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={{ pageSize: 10 }}
        size='middle'
      />
    </Card>
  )
}

export function PinnacleDashboard(): JSX.Element {
  const { data, isLoading, isError, error, refetch, isFetching } = usePinnacleHealth()

  const files = data?.files ?? []
  const summary = data?.summary

  const getDaysSinceColor = () => {
    if (!summary) return undefined
    if (summary.is_stale) return '#cf1322'
    if (summary.days_since_last_file === 0) return '#3f8600'
    if (summary.days_since_last_file === 1) return '#faad14'
    return '#3f8600'
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Pinnacle BAI Files Status</Title>
      <Space direction='vertical' size={16} style={{ width: '100%' }}>
        {isError && (
          <Alert
            type='error'
            showIcon
            message='Failed to load'
            description={(error as Error)?.message ?? String(error)}
          />
        )}

        {summary?.is_stale && (
          <Alert
            type='error'
            showIcon
            message='⚠️ CRITICAL: No Recent BAI Files'
            description='No new BAI files received from the bank on working days. Please verify bank is sending files and check SFTP connectivity.'
          />
        )}

        {summary && summary.files_with_missing_copies > 0 && (
          <Alert
            type='warning'
            showIcon
            message='⚠️ WARNING: Missing Environment Copies'
            description={`${summary.files_with_missing_copies} file(s) are missing environment copies (prod/qa/dev). Check workflow logs and S3 permissions.`}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Total Files (7d)'
                value={summary?.total_files || 0}
                prefix={<FileTextOutlined />}
                loading={isLoading || isFetching}
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Missing Copies'
                value={summary?.files_with_missing_copies || 0}
                loading={isLoading || isFetching}
                valueStyle={{
                  color: (summary?.files_with_missing_copies || 0) > 0 ? '#cf1322' : '#3f8600'
                }}
                prefix={
                  (summary?.files_with_missing_copies || 0) > 0 ? (
                    <WarningOutlined />
                  ) : (
                    <CheckCircleOutlined />
                  )
                }
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Days Since Last File'
                value={
                  summary?.days_since_last_file !== undefined && summary.days_since_last_file >= 0
                    ? summary.days_since_last_file
                    : '—'
                }
                loading={isLoading || isFetching}
                valueStyle={{ color: getDaysSinceColor() }}
              />
            </Card>
          </Col>
          <Col xs={24} md={6}>
            <Card className='rounded-2xl'>
              <Statistic
                title='Status'
                value={summary?.is_stale ? 'STALE' : 'OK'}
                loading={isLoading || isFetching}
                valueStyle={{
                  color: summary?.is_stale ? '#cf1322' : '#3f8600'
                }}
                prefix={summary?.is_stale ? <WarningOutlined /> : <CheckCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card className='rounded-2xl'>
              <Space>
                <InfoCircleOutlined />
                <Typography.Text>
                  Last file received: {fmtDateTime(summary?.last_file_date)} • Expected
                  environments: {summary?.expected_environments.join(', ')} • Window:{' '}
                  {fmtDateTime(data?.range.from)} → {fmtDateTime(data?.range.to)}
                </Typography.Text>
              </Space>
            </Card>
          </Col>
        </Row>

        <FilesTable
          data={files}
          expectedEnvs={summary?.expected_environments ?? []}
          loading={isLoading || isFetching}
          onReload={refetch}
        />
      </Space>
    </div>
  )
}
